// components/Common/Modal/Modal.tsx
import React, { ReactNode, useEffect, useRef, useState } from 'react';
import styles from './XadomModal.module.scss';

interface XadomModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string | ReactNode;
  children?: React.ReactNode;
  className?: string;
  closeOnClickOutside?: boolean;
  closeOnEscape?: boolean;
  closeButton?: boolean;
  zIndex?: number;
}

export const XadomModal: React.FC<XadomModalProps> = ({ 
  isOpen, 
  onClose, 
  title, 
  children, 
  className = '',
  closeOnClickOutside = true,
  closeOnEscape = true,
  closeButton = true,
  zIndex = 2000,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const previousActiveElementRef = useRef<HTMLElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [modalPosition, setModalPosition] = useState({ x: 0, y: 0 });
  const hasInitialFocusRef = useRef(false);


  // 드래그 시작
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!modalRef.current) return;
    
    setDragStart({
      x: e.clientX - modalPosition.x,
      y: e.clientY - modalPosition.y
    });
    setIsDragging(true);
  };

  // 드래그 중
  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging) return;
    
    const newX = e.clientX - dragStart.x;
    const newY = e.clientY - dragStart.y;
    
    setModalPosition({ x: newX, y: newY });
  };

  // 드래그 종료
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 드래그 이벤트 리스너 등록
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  // 모달의 열림 상태(isOpen)에 따른 모든 부수 효과를 관리하는 통합 useEffect
  useEffect(() => {
    if (!isOpen) {
      hasInitialFocusRef.current = false;
      return;
    }

    // --- 1. 모달이 열릴 때의 처리 ---

    // 이전에 활성화된 요소 저장
    previousActiveElementRef.current = document.activeElement as HTMLElement;

    // body 스크롤 막기는 첫 번째 모달에서만 실행
    const existingModals = document.querySelectorAll('[role="dialog"]');
    if (existingModals.length <= 1) {
      document.body.style.overflow = 'hidden';
    }
    
    // 모달 위치 초기화
    setModalPosition({ x: 0, y: 0 });

    // 모달이 열릴 때는 포커스를 강제로 이동시키지 않음
    // 단지 초기화 플래그만 설정
    if (!hasInitialFocusRef.current) {
      hasInitialFocusRef.current = true;
    }
    const focusTimeoutId = null;

    // --- 2. 이벤트 핸들러 정의 및 등록 ---

    // Escape 키를 눌렀을 때 모달 닫기
    const handleEscape = (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };

    // 모달 외부를 클릭했을 때 모달 닫기
    const handleClickOutside = (e: MouseEvent) => {
      if (closeOnClickOutside && overlayRef.current === e.target) {
        // [핵심] 기본 동작을 막아 포커스가 body로 가는 것을 방지
        e.preventDefault();
        onClose();
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    // --- 3. 정리(Cleanup) 함수: 모달이 닫히거나 언마운트될 때 실행 ---
    return () => {
      if (focusTimeoutId) {
        clearTimeout(focusTimeoutId);
      }

      // 등록했던 이벤트 리스너 모두 제거
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);

      // body 스크롤 복원은 마지막 모달이 닫힐 때만 실행
      const remainingModals = document.querySelectorAll('[role="dialog"]');
      if (remainingModals.length <= 1) {
        document.body.style.overflow = 'unset';
      }

      // [핵심] 이전에 활성화됐던 요소로 포커스 복원
      try {
        if (previousActiveElementRef.current && typeof previousActiveElementRef.current.focus === 'function') {
          previousActiveElementRef.current.focus();
        }
      } catch (error) {
        console.error("모달 포커스 복원에 실패했습니다.", error);
      }
    };
  }, [isOpen, onClose, closeOnClickOutside, closeOnEscape]); // 의존성 배열

  if (!isOpen) return null;

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    zIndex: zIndex,
  };

  return (
    <div 
      ref={overlayRef}
      className={styles['modal-overlay']}
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      <div 
        ref={modalRef} 
        className={`${styles['modal-container']} ${className}`}
        style={{
          transform: `translate(${modalPosition.x}px, ${modalPosition.y}px)`,
          cursor: isDragging ? 'grabbing' : 'default'
        }}
        tabIndex={-1} // 프로그래밍적으로 포커스를 받을 수 있도록 설정
      >
        {(title || closeButton) && (
          <div 
            ref={headerRef}
            className={styles['modal-header']}
            onMouseDown={handleMouseDown}
            style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          >
            {title && (
              <h2 id="modal-title" className={styles['modal-title']}>
                {title}
              </h2>
            )}
            {closeButton && (
              <button
                className={styles['modal-close']}
                onClick={onClose}
                aria-label="모달 닫기"
                type="button"
                onMouseDown={(e) => e.stopPropagation()} // 닫기 버튼 클릭 시 드래그 방지
              >
                ✕
              </button>
            )}
          </div>
        )}
        <div className={styles['modal-content']}>
          {children}
        </div>
      </div>
    </div>
  );
};